import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, IconButton, InputAdornment, Typography } from '@mui/material'
import {
  refreshUserProfile,
  useCreatePassword,
  useReadUserProfile,
} from '@/api/user-manager'
import { toast } from '@/helpers'
import { routes } from '@/router'
import { Loading } from '@/components'
import { sessionButtonSx } from '../session-button'
import { SessionField, SessionScreen } from '../session-screen'
import { flowProgress, nextProfileStep } from '../profile/profile-steps'
import {
  SignupPasswordFormData,
  passwordRules,
  signupPasswordSchema,
} from './signup.schema'
import { useSignupDraft } from './use-signup-draft'

export function SignupPasswordPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const { data: user, isLoading } = useReadUserProfile()
  const { draft, clearDraft } = useSignupDraft()

  const { handleSubmit, control, watch } = useForm<SignupPasswordFormData>({
    resolver: zodResolver(signupPasswordSchema),
    defaultValues: { password: '' },
  })

  const password = watch('password')
  const rules = passwordRules(password)
  const passed = rules.filter((rule) => rule[1]).length
  const isValid = signupPasswordSchema.safeParse({ password }).success

  const { mutateAsync: createPassword, isPending } = useCreatePassword()

  if (isLoading) return <Loading />

  if (user?.has_password) {
    const step = nextProfileStep(user)
    return (
      <Navigate
        to={step ? routes.session.profile[step] : routes.home}
        replace
      />
    )
  }

  const strengthLabel = !password
    ? 'Sua senha precisa ter:'
    : passed <= 1
      ? 'Senha fraca'
      : passed <= 3
        ? 'Senha média'
        : 'Senha forte'

  const strengthColor = !password
    ? 'divider'
    : passed <= 1
      ? 'error.main'
      : passed <= 3
        ? 'warning.main'
        : 'success.main'

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(async ({ password: nextPassword }) => {
        await createPassword({ password: nextPassword })
        const refreshed = await refreshUserProfile()
        clearDraft()
        toast.success('Conta criada')
        const step = nextProfileStep(refreshed)
        navigate(step ? routes.session.profile[step] : routes.home, {
          state: { accountCreated: true },
        })
      })}
      sx={{ display: 'flex', flex: 1 }}
    >
      <SessionScreen
        title="Crie uma senha"
        subtitle="Você vai usar essa senha para entrar no app."
        progress={flowProgress('password', user ?? null)}
        canBack={Boolean(draft.email)}
        onBack={() => navigate(routes.session.signup.code)}
        footer={
          <LoadingButton
            type="submit"
            size="large"
            fullWidth
            loading={isPending}
            disabled={!isValid}
            sx={sessionButtonSx()}
          >
            Criar conta
          </LoadingButton>
        }
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <SessionField
              {...field}
              id="signup-password"
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              autoFocus
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? 'Ocultar senha' : 'Mostrar senha'
                        }
                        onClick={() => setShowPassword((current) => !current)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              height: 4,
              borderRadius: 1,
              bgcolor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: password ? `${Math.max(passed, 1) * 25}%` : 0,
                bgcolor: strengthColor,
                transition: 'width .25s, background .25s',
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {strengthLabel}
            {password && passed < 4 ? '. Falta:' : ''}
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {rules.map(([label, ok]) => (
              <Box
                component="li"
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.88rem',
                  color: ok ? 'text.primary' : 'text.secondary',
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '1.5px solid',
                    borderColor: ok ? 'success.main' : 'divider',
                    bgcolor: ok ? 'success.main' : 'transparent',
                    flex: 'none',
                  }}
                />
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </SessionScreen>
    </Box>
  )
}
